<script setup lang="ts">
import {ref} from "vue";
import { Badge } from '@/Components/ui/badge'
import { GraduationCap, Award, Check } from 'lucide-vue-next'
import PricingPlans from '@/Components/Tutors/PricingPlans.vue'
import AvailabilityCalendar from '@/Components/Tutors/AvailabilityCalendar.vue'
import {truncate} from "lodash";

interface Subject {
  id: number
  name: string
  color: string
}

interface Examboard {
  id: number
  name: string
}

interface ALevel {
  subject: string
  grade: string
  color?: string
}

interface Degree {
  degree_type?: string
  title?: string
  institution?: string
  start_year?: string
  end_year?: string
  status?: string
  classification?: string
}

interface AdditionalQualification {
  name: string
  year?: string
}

interface AcademicQualifications {
  degrees: Degree[]
  alevels: ALevel[]
  years_of_experience?: number | string
  additional_qualifications: AdditionalQualification[]
}

interface TimeSlot {
  day: string
  time: string
  available: boolean
  booked?: boolean
  locked?: boolean
}

interface LegendItem {
  label: string
  bg: string
}

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  tutor: {
    id: number
    slug: string
    first_name: string
    about_me?: string
    subjects: Subject[]
    examboards: Examboard[]
    academic_qualifications: AcademicQualifications
    services_offered: string[]
    price_base_tier: number
    price_silver_tier: number
    price_gold_tier: number
    videos_count: number
    group_subjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
  }
  availability: TimeSlot[][]
  legend?: LegendItem[]
  subscriptions?: Subscription[]
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

function getSubjectBadgeStyles(color: string) {
  const rgb = hexToRgb(color)
  if (!rgb) return { backgroundColor: '#EFF6FF', color: '#1E40AF' }

  return {
    backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`,
    color: color
  }
}

const props = withDefaults(defineProps<Props>(), {
  subscriptions: () => [],
})

const maxLength = 420;
const aboutFullDisplayed = ref<Boolean>(false);

const successStats = [
  { value: '95%', label: 'A*/A Grades', gradient: 'from-teal-500 to-teal-600', lightColor: 'text-teal-100' },
  { value: '1,247', label: 'Total Students', gradient: 'from-blue-500 to-blue-600', lightColor: 'text-blue-100' },
  { value: '156', label: 'Video Lessons', gradient: 'from-purple-500 to-purple-600', lightColor: 'text-purple-100' },
  { value: '4.9', label: 'Average Rating', gradient: 'from-pink-500 to-pink-600', lightColor: 'text-pink-100' }
]

</script>

<template>
  <div class="space-y-8">
    <!-- About Section -->
    <div>
      <h3 class="text-xl font-semibold text-gray-900 mb-4">About {{ tutor.first_name }}</h3>
      <p class="text-gray-600 leading-relaxed mb-4 whitespace-pre-line">
        {{
              ( aboutFullDisplayed ? tutor.about_me : truncate(tutor.about_me, {length: maxLength}))
              || `Experienced tutor with a passion for helping students achieve their academic goals.` }}
          <a href="#" v-if="!aboutFullDisplayed && tutor.about_me?.length > maxLength" @click.prevent="aboutFullDisplayed = !aboutFullDisplayed" class="text-blue-600 hover:underline">
              Read more
          </a>
      </p>
      <div class="flex flex-wrap gap-2 mt-4">
        <span
          v-for="subject in tutor.subjects"
          :key="subject.id"
          :style="getSubjectBadgeStyles(subject.color)"
          class="px-4 py-2 rounded-lg text-sm font-medium"
        >
          {{ subject.name }}
        </span>
      </div>
    </div>

    <!-- Exam Boards Section -->
    <div v-if="tutor.examboards.length > 0">
      <h3 class="text-xl font-semibold text-gray-900 mb-4">Exam Boards Covered</h3>
      <div class="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
        <div class="grid grid-cols-4 gap-4">
          <div
            v-for="examboard in tutor.examboards"
            :key="examboard.id"
            class="bg-white rounded-lg p-4 text-center border border-gray-200 hover:border-blue-600 transition-colors"
          >
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <GraduationCap class="w-6 h-6 text-blue-600" />
            </div>
            <h4 class="font-semibold text-gray-900 text-sm truncate">{{ examboard.name }}</h4>
          </div>
        </div>
      </div>
    </div>

    <!-- Qualifications & Experience -->
    <div v-if="tutor.academic_qualifications && (tutor.academic_qualifications.degrees?.length > 0 || tutor.academic_qualifications.alevels?.length > 0)">
      <h3 class="text-xl font-semibold text-gray-900 mb-4">Qualifications & Experience</h3>

      <!-- Years of Experience -->
      <div v-if="tutor.academic_qualifications.years_of_experience" class="mb-4 flex items-center gap-2">
        <Award class="w-5 h-5 text-blue-600" />
        <span class="text-gray-700">{{ tutor.academic_qualifications.years_of_experience }} years of teaching experience</span>
      </div>

      <!-- University Degrees -->
      <div v-if="tutor.academic_qualifications.degrees?.length > 0" class="space-y-4 mb-6">
        <div
          v-for="(degree, index) in tutor.academic_qualifications.degrees"
          :key="index"
          class="flex items-start gap-4"
        >
          <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <GraduationCap class="w-6 h-6 text-blue-600" />
          </div>
          <div class="flex-1">
            <h4 class="font-semibold text-gray-900">
              {{ degree.degree_type }} {{ degree.title }}
              <span v-if="degree.institution"> - {{ degree.institution }}</span>
            </h4>
            <p class="text-sm text-gray-600">
              <span v-if="degree.classification">{{ degree.classification }}</span>
              <span v-if="degree.classification && (degree.start_year || degree.end_year)">, </span>
              <span v-if="degree.start_year || degree.end_year">
                {{ degree.start_year }}<span v-if="degree.start_year && degree.end_year"> - </span>{{ degree.end_year }}
              </span>
              <span v-if="degree.status === 'in_progress'" class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                In Progress
              </span>
            </p>
          </div>
        </div>
      </div>

      <!-- A-Level Results -->
      <div v-if="tutor.academic_qualifications.alevels?.length > 0">
        <p class="text-sm font-medium text-gray-700 mb-2">A-Level Results:</p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="(alevel, idx) in tutor.academic_qualifications.alevels"
            :key="idx"
            :style="getSubjectBadgeStyles(alevel.color || '#3B82F6')"
            class="px-3 py-1.5 rounded-lg text-sm font-medium"
          >
            {{ alevel.subject }} <span class="font-bold">{{ alevel.grade }}</span>
          </span>
        </div>
      </div>

      <!-- Additional Qualifications -->
      <div v-if="tutor.academic_qualifications.additional_qualifications?.length > 0" class="mt-4">
        <p class="text-sm font-medium text-gray-700 mb-2">Additional Qualifications:</p>
        <ul class="list-disc list-inside text-sm text-gray-600">
          <li v-for="(qual, idx) in tutor.academic_qualifications.additional_qualifications" :key="idx">
            {{ qual.name }}<span v-if="qual.year"> ({{ qual.year }})</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Pricing Plans -->
    <PricingPlans
      :tutor-slug="tutor.slug"
      :tutor-id="tutor.id"
      :base-price="tutor.price_base_tier"
      :silver-price="tutor.price_silver_tier"
      :gold-price="tutor.price_gold_tier"
      :services-offered="tutor.services_offered"
      :videos-count="tutor.videos_count"
      :subjects="tutor.subjects"
      :group-subjects="tutor.group_subjects"
      :subscriptions="subscriptions"
    />

    <!-- Availability Calendar -->
    <AvailabilityCalendar
      :tutor-name="tutor.first_name"
      :availability="availability"
      :legend="legend"
    />

    <!-- Success Stats -->
    <!-- <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div
        v-for="(stat, index) in successStats"
        :key="index"
        :class="['rounded-xl p-6 text-white bg-gradient-to-br', stat.gradient]"
      >
        <div class="text-3xl font-bold mb-1">{{ stat.value }}</div>
        <div :class="['text-sm', stat.lightColor]">{{ stat.label }}</div>
      </div>
    </div> -->
  </div>
</template>
